import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookmarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import EndpointRunner from '@/components/runner/EndpointRunner';
import { useFlag } from '@/hooks/useFeatureFlags';
import { TestsPanel } from '@/components/endpoints/tests/TestsPanel';
import { GenerateTestsFlow, OpenGenerateTestsFlowButton } from '@/components/endpoints/tests/GenerateTestsFlow';
import { SaveAsTestDialog } from '@/components/endpoints/tests/SaveAsTestDialog';
import { Endpoint } from '@/types';
import type { EndpointTest } from '@/types/endpointTests';
import { serviceRunPath, serviceRunsTabPath } from '@/components/runExplorer/runExplorerLinks';

interface EndpointTestTabProps {
  endpoint: Endpoint;
}

interface ExecutionHistoryItem {
  id: string;
  type: 'sync' | 'async';
  success: boolean;
  timestamp: string;
  collaborationId?: string;
}

/**
 * The endpoint hub's Test tab. Wraps the shared EndpointRunner locked to this
 * endpoint. With `innovate.fe.endpoints.tests` on (task 365), the right column
 * is the persistent Tests panel (saved tests + generation flow) and the runner
 * gains "Save as test"; with the flag off it stays the original ephemeral
 * Recent Runs card. After a run, each entry links to the produced run inside
 * the hub: the nested run route when the collaboration id is known, otherwise
 * the Runs tab.
 */
export function EndpointTestTab({ endpoint }: EndpointTestTabProps) {
  const [key, setKey] = useState(0);
  const [executionHistory, setExecutionHistory] = useState<ExecutionHistoryItem[]>([]);
  const testsEnabled = useFlag('innovate.fe.endpoints.tests');
  // Save as scenario belongs to the test runs feature set (task 1215).
  const saveAsEnabled = useFlag('innovate.fe.testing.runs');
  // A saved test's input loaded into the runner (consumed on runner remount).
  const [loadedInput, setLoadedInput] = useState<Record<string, unknown> | undefined>(undefined);
  const [generateOpen, setGenerateOpen] = useState(false);
  // The runner input captured when "Save as test" was clicked; non-null = dialog open.
  const [saveDialogInput, setSaveDialogInput] = useState<Record<string, unknown> | null>(null);

  // Force re-render of EndpointRunner when endpoint changes
  // This ensures the runner is reset with the correct endpoint
  useEffect(() => {
    setKey(prev => prev + 1);
    setExecutionHistory([]);
    setLoadedInput(undefined);
    setGenerateOpen(false);
    setSaveDialogInput(null);
  }, [endpoint.id]);

  // Load a saved test's input into the runner: seed initialInput and bump the
  // key so the runner remounts and consumes it.
  const handleLoadIntoRunner = (test: EndpointTest) => {
    setGenerateOpen(false);
    setLoadedInput(test.input);
    setKey(prev => prev + 1);
  };

  const handleExecutionComplete = (result: {
    type: 'sync' | 'async';
    success: boolean;
    timestamp: string;
    collaborationId?: string;
  }) => {
    setExecutionHistory(prev => {
      // Async runs report twice (queued, then completed). When the second
      // report carries the collaboration id, enrich the existing entry rather
      // than appending a duplicate.
      if (result.collaborationId) {
        const matchIndex = prev.findIndex(
          item => item.type === result.type && !item.collaborationId
        );
        if (matchIndex !== -1) {
          const next = [...prev];
          next[matchIndex] = {
            ...next[matchIndex],
            success: result.success,
            collaborationId: result.collaborationId,
          };
          return next;
        }
      }
      const item: ExecutionHistoryItem = {
        id: crypto.randomUUID(),
        type: result.type,
        success: result.success,
        timestamp: result.timestamp,
        collaborationId: result.collaborationId,
      };
      return [item, ...prev].slice(0, 10);
    });
  };

  const runsTabPath = serviceRunsTabPath(endpoint.id);

  // "Save as test" beside Execute (flag-gated): captures the runner's current
  // parsed input and opens the naming dialog.
  const renderRunActions = (getCurrentInput: () => Record<string, unknown> | null) => (
    <button
      type="button"
      onClick={() => {
        const input = getCurrentInput();
        if (input) setSaveDialogInput(input);
      }}
      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
    >
      <BookmarkIcon className="h-4 w-4 mr-2" />
      Save as scenario
    </button>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        {testsEnabled && generateOpen ? (
          <GenerateTestsFlow endpoint={endpoint} onClose={() => setGenerateOpen(false)} />
        ) : (
          <EndpointRunner
            key={key}
            lockedEndpointId={endpoint.id}
            onExecutionComplete={handleExecutionComplete}
            initialInput={testsEnabled ? loadedInput : undefined}
            renderRunActions={testsEnabled && saveAsEnabled ? renderRunActions : undefined}
          />
        )}
      </div>

      <div className="lg:col-span-1">
        {testsEnabled ? (
          <TestsPanel
            endpoint={endpoint}
            onLoadIntoRunner={handleLoadIntoRunner}
            renderGenerate={(placement) => (
              <OpenGenerateTestsFlowButton placement={placement} onOpen={() => setGenerateOpen(true)} />
            )}
          />
        ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-gray-400" />
            Recent Runs
          </h3>

          {executionHistory.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No runs yet. Fill in the input and click Run to try this endpoint.
            </p>
          ) : (
            <div className="space-y-3">
              {executionHistory.map((item) => (
                <div
                  key={item.id}
                  className={`p-3 rounded-lg border ${
                    item.success
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`w-2 h-2 rounded-full ${
                        item.success ? 'bg-green-500' : 'bg-red-500'
                      }`} />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {item.success ? 'Success' : 'Failed'}
                      </span>
                      {item.type === 'async' && (
                        <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                          Async
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(item.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="mt-2">
                    <Link
                      to={item.collaborationId
                        ? serviceRunPath(endpoint.id, item.collaborationId)
                        : runsTabPath}
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      {item.collaborationId ? 'View run' : 'View in Runs'}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      {testsEnabled && saveDialogInput && (
        <SaveAsTestDialog
          endpointId={endpoint.id}
          input={saveDialogInput}
          onClose={() => setSaveDialogInput(null)}
        />
      )}
    </div>
  );
}
